import Router from 'next/router';
import getConfig from 'next/config';
import { useCallback, useContext, useEffect, useState } from 'react';
import { fetchState } from '../../context/UserContext';
import useUser from '../../context/UserContext/useUser';
import LocationContext from '../../context/LocationContext/LocationContext';
import {
  identifyClarity,
  initClarity,
  setClarityTag,
  trackClarityEvent,
} from '../../util/clarity';

function getPagePath(pathOrUrl) {
  if (typeof window === 'undefined') return '';

  try {
    return new URL(
      pathOrUrl || window.location.pathname,
      window.location.origin,
    ).pathname;
  } catch (error) {
    return window.location.pathname;
  }
}

const ClarityAnalytics = () => {
  const {
    publicRuntimeConfig: {
      GF_DOTCOM_CLARITY_PROJECT_ID,
    },
  } = getConfig();
  const { user, userFetchState } = useUser();
  const { country, state } = useContext(LocationContext);
  const [pagePath, setPagePath] = useState('');
  const userId = user?.uuid;

  const trackPage = useCallback((pathOrUrl) => {
    const nextPagePath = getPagePath(pathOrUrl);
    setPagePath(nextPagePath);
    setClarityTag(
      GF_DOTCOM_CLARITY_PROJECT_ID,
      'page_path',
      nextPagePath,
    );
    trackClarityEvent(GF_DOTCOM_CLARITY_PROJECT_ID, 'page_view');
  }, [GF_DOTCOM_CLARITY_PROJECT_ID]);

  useEffect(() => {
    if (!GF_DOTCOM_CLARITY_PROJECT_ID) return undefined;

    initClarity(GF_DOTCOM_CLARITY_PROJECT_ID);
    trackPage();

    const handleRouteChangeComplete = (url) => {
      trackPage(url);
    };

    Router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [GF_DOTCOM_CLARITY_PROJECT_ID, trackPage]);

  useEffect(() => {
    if (!GF_DOTCOM_CLARITY_PROJECT_ID) return;

    if (country) {
      setClarityTag(GF_DOTCOM_CLARITY_PROJECT_ID, 'country', country);
    }

    if (state) {
      setClarityTag(GF_DOTCOM_CLARITY_PROJECT_ID, 'state', state);
    }
  }, [GF_DOTCOM_CLARITY_PROJECT_ID, country, state]);

  useEffect(() => {
    if (!GF_DOTCOM_CLARITY_PROJECT_ID || !pagePath) return;
    if (userFetchState === fetchState.PENDING) return;

    const isAuthenticated = !!userId;
    setClarityTag(
      GF_DOTCOM_CLARITY_PROJECT_ID,
      'auth_state',
      isAuthenticated ? 'authenticated' : 'anonymous',
    );

    if (isAuthenticated) {
      identifyClarity(
        GF_DOTCOM_CLARITY_PROJECT_ID,
        userId,
        undefined,
        pagePath,
      );
    }
  }, [GF_DOTCOM_CLARITY_PROJECT_ID, pagePath, userFetchState, userId]);

  return null;
};

export default ClarityAnalytics;
